import TicketSaleCard from 'app/components/TicketSaleCard/TicketSaleCard';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { clearCartData } from 'store/cartSlice';
import { selectCartData } from 'store/cartSlice/selectors';
import {
  removeAllTheSelectedItems,
  removeQueryParams,
  resetVoucherData,
  setAllTheSelectedItems,
  setQueryParams,
} from '../TicketSalePage/slice';
import {
  makeSelectedEvent,
  selectSelectedTickets,
  selectTicketIdsFromQueryParams,
  selectTicketSaleIsLoading,
} from '../TicketSalePage/slice/selectors';
import useEventWithTickets from '../TicketSalePage/useEventWithTickets';

import CartCountdownTimer from 'app/components/CartCountdownTimer/CartCountdownTimer';
import SeatingChartWrapper from 'app/components/SeatingChartWrapper/SeatingChartWrapper';
import SeatsCountdownTimer from 'app/components/SeatsCountdownTimer/SeatsCountdownTimer';
import CartCountdownTimerSkeleton from 'app/components/Skeletons/Common/CartCountdownTimer';
import CartStepCardSkeleton from 'app/components/Skeletons/Common/CartStepCardSkeleton';
import CartStepsSkeleton from 'app/components/Skeletons/Common/CartStepsSkeleton';
import TicketSalePageHeader from 'app/components/TicketSalePageHeader/TicketSalePageHeader';
import TicketSalePageMeta from '../TicketSalePage/TicketSalePageMeta';
import useToastError from '../TicketSalePage/useToastError';
import useViewItemListGTM from '../TicketSalePage/useViewItemListGTM';
import { Ticket } from 'types/TicketsState';
import { CategoryKey } from '@seatsio/seatsio-react';
import { useTranslation } from 'react-i18next';

const ReserveSeatsPage: React.FC<{}> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const pageDataIsLoading = useSelector(selectTicketSaleIsLoading);
  const selectedEvent = useSelector(makeSelectedEvent);
  const selectedTickets = useSelector(selectSelectedTickets);
  const {
    loading: cartIsLoading,
    selectedEvent: cartEvent,
    selectedEvent: { id: cartEventId },
    selectedItems: cartItems,
    countdown: { endTime, onGoing },
  } = useSelector(selectCartData);
  const ticketIdsFromQueryParamsStore = useSelector(
    selectTicketIdsFromQueryParams,
  );
  const [countdownIsOnGoing, setCountdownIsOnGoing] = useState(false);
  const [chartIsOpen, setChartIsOpen] = useState(false);

  const handleCloseChart = () => {
    setChartIsOpen(false);
  };

  const handleOpenChart = () => {
    setChartIsOpen(true);
  };

  useEventWithTickets({ extraIncludes: ['seatingChart', 'tickets.subtypes'] });

  useToastError();

  useViewItemListGTM(selectedEvent);

  useEffect(() => {
    let secondsRemaining;
    if (endTime) {
      secondsRemaining = Math.round(
        DateTime.fromISO(endTime).diffNow('seconds').seconds,
      );
    } else {
      secondsRemaining = 0;
    }
    // remove persisted data if there is a cart and it is expired
    if (secondsRemaining <= 0 && cartEventId) {
      dispatch(clearCartData());
    } else if (cartEventId && selectedEvent.id !== cartEventId) {
      // remove selected tickets & voucher if there is an ongoing cart with another event
      dispatch(removeAllTheSelectedItems());
      dispatch(resetVoucherData());
    } else if (
      cartEventId === selectedEvent.id &&
      cartItems.length > 0 &&
      selectedTickets.length === 0
    ) {
      // automatically select tickets if there is an ongoing cart with the current event
      dispatch(setAllTheSelectedItems({ cartItems, cartEvent }));
    }

    const ticketIdsFromQueryParams = searchParams.get('tickets');

    if (ticketIdsFromQueryParams) {
      dispatch(setQueryParams(ticketIdsFromQueryParams));
    } else if (ticketIdsFromQueryParamsStore) {
      dispatch(removeQueryParams());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent.id]);

  const availableCategories = getAvailableCategories(
    selectedEvent.tickets,
    selectedEvent.dateTimeFormat.timezone,
  );

  return (
    <div className="page-container">
      <TicketSalePageMeta />
      {cartIsLoading ? (
        <CartCountdownTimerSkeleton />
      ) : (
        <div className="flex flex-col justify-between items-start gap-4 sm:flex-row sm:items-center px-4 mt-4">
          <TicketSalePageHeader />
          {onGoing ? (
            <CartCountdownTimer />
          ) : countdownIsOnGoing ? (
            <SeatsCountdownTimer />
          ) : null}
        </div>
      )}
      <div className="section-container">
        {cartIsLoading ? (
          <>
            <CartStepsSkeleton />
            <CartStepCardSkeleton />
          </>
        ) : (
          <div className="flex flex-col-reverse md:flex-row">
            <div
              id="seating-chart"
              className="shadow-[0_4px_26px_rgba(0,0,0,0.02)] bg-white md:h-[540px] md:w-[65%] flex justify-center items-center"
            >
              {pageDataIsLoading ? null : availableCategories.length === 0 ? (
                <h2 className="p-[12px] text-center">
                  {t('ticketSale.thereAreNotTicketsAvailable')}
                </h2>
              ) : (
                <SeatingChartWrapper
                  setCountdownIsOnGoing={setCountdownIsOnGoing}
                  chartIsOpen={chartIsOpen}
                  handleCloseChart={handleCloseChart}
                  availableCategories={availableCategories}
                />
              )}
            </div>
            <div className="md:w-[35%] rounded-lg shadow-md">
              <TicketSaleCard handleOpenChart={handleOpenChart} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReserveSeatsPage;

const getAvailableCategories = (
  tickets: Ticket[],
  timezone: string,
): CategoryKey[] => {
  const now = DateTime.now().setZone(timezone);

  return tickets
    .filter(ticket => {
      if (!ticket.sold_from) return true;

      const soldFrom = DateTime.fromISO(ticket.sold_from);

      return now.diff(soldFrom, 'milliseconds').toMillis() >= 0;
    })
    .map(ticket => ticket.seatsCategoryKey);
};
